import React, { useState, useEffect } from 'react';
import { Sprout, Sun, Droplets, TreePine, Flower2, Crown } from 'lucide-react';
import { apiClient } from '../../api/client';

const PLANT_VISUALS = {
  bare_soil: { icon: Droplets, color: '#a3a3a3', bg: '#f5f5f5', emoji: '' },
  seed: { icon: Droplets, color: '#a3e635', bg: '#f7fee7', emoji: '' },
  sprout: { icon: Sprout, color: '#22c55e', bg: '#f0fdf4', emoji: '' },
  sapling: { icon: Sprout, color: '#10b981', bg: '#ecfdf5', emoji: '' },
  young_tree: { icon: TreePine, color: '#059669', bg: '#d1fae5', emoji: '' },
  flowering: { icon: Flower2, color: '#ec4899', bg: '#fdf2f8', emoji: '' },
  full_tree: { icon: Crown, color: '#f59e0b', bg: '#fffbeb', emoji: '' },
};

const MoodStreakGardenCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStages, setShowStages] = useState(false);

  useEffect(() => {
    apiClient.request('/api/enhancements/mood-streak')
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div data-testid="mood-streak-card" className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-32 mb-4" />
        <div className="h-32 bg-gray-100 rounded" />
      </div>
    );
  }

  if (!data) return null;

  const { current_streak, longest_streak, checked_in_today, garden, garden_health, next_stage, days_to_next, all_stages, recent_moods } = data;
  const visual = PLANT_VISUALS[garden.plant] || PLANT_VISUALS.bare_soil;
  const PlantIcon = visual.icon;

  // Build mini sparkline from recent moods
  const moodValues = (recent_moods || []).map(m => m.mood_score || 0);
  const maxMood = Math.max(...moodValues, 5);
  const sparklinePath = moodValues.length > 1
    ? moodValues.map((v, i) => {
        const x = (i / (moodValues.length - 1)) * 100;
        const y = 100 - (v / maxMood) * 80 - 10;
        return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
      }).join(' ')
    : null;

  return (
    <div data-testid="mood-streak-card" className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: visual.bg, color: visual.color }}>
              <PlantIcon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Mood Garden</h3>
              <p className="text-xs text-gray-400">{garden.label}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold" style={{ color: visual.color }}>{current_streak}</p>
            <p className="text-xs text-gray-400">day streak</p>
          </div>
        </div>

        {/* Garden visualization */}
        <div className="rounded-xl p-4 mb-4 relative overflow-hidden" style={{ background: `linear-gradient(180deg, #e0f2fe 0%, ${visual.bg} 100%)` }}>
          {/* Sun */}
          <div className="absolute top-2 right-3">
            <Sun className="w-8 h-8 text-yellow-400 opacity-60" />
          </div>

          {/* Plant */}
          <div className="flex flex-col items-center py-4">
            <PlantIcon className="w-16 h-16 transition-all duration-500" style={{ color: visual.color }} />
            <p className="text-sm font-semibold mt-2" style={{ color: visual.color }}>{garden.label}</p>
            <p className="text-xs text-gray-500 mt-0.5">{garden.description}</p>
          </div>

          {/* Health bar */}
          <div className="mt-2">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>Garden Health</span>
              <span>{garden_health}%</span>
            </div>
            <div className="w-full h-1.5 bg-white/50 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{
                width: `${garden_health}%`,
                background: garden_health > 60 ? '#22c55e' : garden_health > 30 ? '#f59e0b' : '#ef4444'
              }} />
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
          <div className="bg-gray-50 rounded-lg p-2.5 text-center">
            <p className="text-sm font-bold text-gray-900">{current_streak}d</p>
            <p className="text-xs text-gray-400">Current</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2.5 text-center">
            <p className="text-sm font-bold text-gray-900">{longest_streak}d</p>
            <p className="text-xs text-gray-400">Best</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2.5 text-center">
            <p className={`text-sm font-bold ${checked_in_today ? 'text-emerald-600' : 'text-amber-600'}`}>
              {checked_in_today ? 'Done' : 'Go!'}
            </p>
            <p className="text-xs text-gray-400">Today</p>
          </div>
        </div>

        {/* Progress to next stage */}
        {next_stage && (
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl mb-4">
            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-gray-400">
              {(() => { const NS = PLANT_VISUALS[next_stage.plant]?.icon || Sprout; return <NS className="w-4 h-4" />; })()}
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-gray-600">Next: {next_stage.label}</p>
              <p className="text-xs text-gray-400">{days_to_next} more day{days_to_next !== 1 ? 's' : ''}</p>
            </div>
          </div>
        )}

        {/* Mood sparkline */}
        {sparklinePath && moodValues.length > 2 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Mood Trend (14 days)</p>
            <svg viewBox="0 0 100 100" className="w-full h-12" preserveAspectRatio="none">
              <defs>
                <linearGradient id="moodGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={visual.color} stopOpacity="0.3" />
                  <stop offset="100%" stopColor={visual.color} stopOpacity="0" />
                </linearGradient>
              </defs>
              {sparklinePath && (
                <>
                  <path d={`${sparklinePath} L 100 100 L 0 100 Z`} fill="url(#moodGrad)" />
                  <path d={sparklinePath} fill="none" stroke={visual.color} strokeWidth="2" strokeLinecap="round" />
                </>
              )}
            </svg>
          </div>
        )}

        {/* Stage timeline toggle */}
        <button
          data-testid="toggle-garden-stages"
          onClick={() => setShowStages(!showStages)}
          className="w-full mt-3 text-xs text-gray-500 hover:text-gray-700 font-medium text-center"
        >
          {showStages ? 'Hide stages' : 'View all growth stages'}
        </button>

        {showStages && (
          <div className="mt-3 space-y-2">
            {all_stages.map(stage => {
              const reached = current_streak >= stage.days;
              const isCurrent = garden.plant === stage.plant;
              const sv = PLANT_VISUALS[stage.plant] || PLANT_VISUALS.bare_soil;
              const SI = sv.icon;
              return (
                <div key={stage.plant} className={`flex items-center gap-3 p-2 rounded-lg ${isCurrent ? 'bg-gray-50 ring-1 ring-gray-200' : ''}`}>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${reached ? '' : 'opacity-30'}`}
                    style={{ background: reached ? sv.bg : '#f3f4f6', color: reached ? sv.color : '#9ca3af' }}>
                    <SI className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1">
                    <p className={`text-xs font-medium ${reached ? 'text-gray-900' : 'text-gray-400'}`}>{stage.label}</p>
                    <p className="text-xs text-gray-400">{stage.days}+ days</p>
                  </div>
                  {isCurrent && <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: `${sv.color}15`, color: sv.color }}>Now</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MoodStreakGardenCard;
